import React, { useCallback, useEffect, useRef } from 'react';
import { Text, Spinner, Center, HStack, VStack, Field, Input, RadioGroup } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useGetRegCustomerQuery, usePatchRegCustomerMutation } from '../../../../api';
import Button from '../../../../components/base/Button';
import { alertManager } from '../../../../utils/alertManager';
import {
    createRegistrationPersonalDetailsSchema,
    RegistrationPersonalDetailsFormData,
} from '../../../../schemas/registrationSchema';
import { hasDiff } from '../../../../utils/hasDiff';
import type { PatchRegCustomer } from '../../../../api/registration/types';
import { RegistrationPassportField, validateRegistrationPassportField } from './RegistrationPassportField';

const normalizeGender = (gender?: string | null) => {
    const normalized = (gender ?? '').toUpperCase();
    if (normalized === 'F' || normalized === 'FEMALE') return 'F';
    return 'M';
};

type RegistrationStepProps = {
    onBack: () => void;
    onNext: () => void;
};

export const StepPersonalDetails: React.FC<RegistrationStepProps> = ({ onBack, onNext }) => {
    const { t } = useTranslation();

    const { data: regCustomer, isLoading } = useGetRegCustomerQuery();
    const [patchRegCustomer, { isLoading: isPatching }] = usePatchRegCustomerMutation();
    const appliedFromApi = useRef(false);

    const {
        register,
        reset,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<RegistrationPersonalDetailsFormData>({
        resolver: yupResolver(createRegistrationPersonalDetailsSchema(t)),
        mode: 'onBlur',
    });

    useEffect(() => {
        if (isLoading || !regCustomer || appliedFromApi.current) {
            return;
        }
        appliedFromApi.current = true;
        reset({
            firstName: regCustomer.first_name ?? '',
            lastName: regCustomer.last_name ?? '',
            gender: normalizeGender(regCustomer.gender),
            phone: regCustomer.phone ?? '',
        });
    }, [isLoading, regCustomer, reset]);

    const handleRegisterClick = useCallback(() => {
        void handleSubmit(async ({ firstName, lastName, gender, phone }) => {
            if (isPatching) return;
            if (!validateRegistrationPassportField()) {
                return;
            }

            const patchPayload: PatchRegCustomer = {
                first_name: firstName,
                last_name: lastName,
                gender,
                phone,
            };

            if (hasDiff<PatchRegCustomer>(regCustomer, patchPayload)) {
                try {
                    await patchRegCustomer(patchPayload).unwrap();
                } catch {
                    alertManager.error(t.registration.errors.serverError);
                    return;
                }
            }

            onNext();
        })();
    }, [handleSubmit, isPatching, onNext, patchRegCustomer, regCustomer]);

    if (isLoading) {
        return (
            <Center py={10} flexDirection="column" gap={4}>
                <Spinner size="lg" color="yellow.600" />
                <Text fontSize="md" color="gray.700" textAlign="center">
                    {t.registration.form.loadingRegCustomerData}
                </Text>
            </Center>
        );
    }

    return (
        <VStack align="stretch" w="full" h="full" minH={0} p={1}>
            <VStack
                gap={4}
                align="stretch"
                flex={1}
                minH={0}
                overflowY="auto"
                p={1}
                css={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                <Field.Root invalid={!!errors.firstName}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.firstNameLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.firstNamePlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.firstName ? 'red' : 'gray'}
                        {...register('firstName')}
                    />
                    {errors.firstName && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.firstName.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root invalid={!!errors.lastName}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.lastNameLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.lastNamePlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.lastName ? 'red' : 'gray'}
                        {...register('lastName')}
                    />
                    {errors.lastName && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.lastName.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root invalid={!!errors.gender}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={0}>
                        {t.registration.form.genderLabel}
                    </Field.Label>
                    <Controller
                        name="gender"
                        control={control}
                        render={({ field }) => (
                            <RadioGroup.Root
                                value={field.value ?? 'M'}
                                onValueChange={(details) => field.onChange(details.value)}
                            >
                                <HStack gap={6}>
                                    <RadioGroup.Item value="M" cursor="pointer">
                                        <RadioGroup.ItemHiddenInput />
                                        <RadioGroup.ItemIndicator />
                                        <RadioGroup.ItemText>
                                            {t.registration.form.genderMale}
                                        </RadioGroup.ItemText>
                                    </RadioGroup.Item>
                                    <RadioGroup.Item value="F" cursor="pointer">
                                        <RadioGroup.ItemHiddenInput />
                                        <RadioGroup.ItemIndicator />
                                        <RadioGroup.ItemText>
                                            {t.registration.form.genderFemale}
                                        </RadioGroup.ItemText>
                                    </RadioGroup.Item>
                                </HStack>
                            </RadioGroup.Root>
                        )}
                    />
                    {errors.gender && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.gender.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root invalid={!!errors.phone}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.phoneLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.phonePlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.phone ? 'red' : 'gray'}
                        {...register('phone')}
                    />
                    {errors.phone && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.phone.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <RegistrationPassportField />
            </VStack>
            <HStack gap={3} justify="space-between" w="full" flexShrink={0} pt={2}>
                <Button buttonType="secondary" size="lg" flex={1} onClick={onBack}>
                    {t.registration.form.back}
                </Button>
                <Button
                    buttonType="success"
                    size="lg"
                    flex={1}
                    isLoading={isPatching}
                    onClick={handleRegisterClick}
                >
                    {t.registration.form.next}
                </Button>
            </HStack>
        </VStack>
    );
};
